import type { WasteCard } from '../data/cards';

const STORAGE_KEY = 'sunhwan-village:miss-counts';

export interface CardMissStat {
  card: WasteCard;
  missCount: number;
}

/**
 * Counts first-attempt misses per card across sessions on this device — a classroom tablet
 * gets handed between students, so the signal accumulates locally rather than per-user.
 */
export class TeacherStats {
  constructor(private readonly storage: Storage = window.localStorage) {}

  recordMiss(cardId: string): void {
    const counts = this.readCounts();
    counts[cardId] = (counts[cardId] ?? 0) + 1;
    this.writeCounts(counts);
  }

  getStats(cards: WasteCard[]): CardMissStat[] {
    const counts = this.readCounts();
    return cards
      .map((card) => ({ card, missCount: counts[card.id] ?? 0 }))
      .filter((stat) => stat.missCount > 0)
      .sort((a, b) => b.missCount - a.missCount);
  }

  reset(): void {
    this.storage.removeItem(STORAGE_KEY);
  }

  private readCounts(): Record<string, number> {
    const raw = this.storage.getItem(STORAGE_KEY);
    if (!raw) return {};
    try {
      return JSON.parse(raw) as Record<string, number>;
    } catch {
      return {};
    }
  }

  private writeCounts(counts: Record<string, number>): void {
    this.storage.setItem(STORAGE_KEY, JSON.stringify(counts));
  }
}
